/**
 * Gráfico de linhas das fichas de qualidade do ar, na moldura comum
 * (`ui/GraficoTempo.tsx`): eixo de tempo em Brasília, marca do instante do
 * quadro, leitura sob o mouse.
 *
 * A escala começa em zero — concentração e índice têm zero de verdade, e uma
 * linha "subindo" de 4 para 6 µg/m³ esticada até o topo pareceria um
 * episódio de fumaça. O topo tem um piso (30 µg/m³ no MP2,5, para a linha da
 * faixa "boa" aparecer) e as linhas de referência das faixas entram quando
 * cabem.
 */
import { useMemo } from 'react';
import { GRAFICO_A, GraficoTempo } from '../ui/GraficoTempo';
import { centroPct } from '../ui/fatias';

export interface Linha {
  valores: readonly (number | null)[];
  cor: string;
  /** Linha tracejada (a média do município sob o sensor). */
  tracejado?: boolean;
}

export interface Referencia {
  valor: number;
  cor: string;
}

/** Trechos contínuos (índices consecutivos com valor): a linha se interrompe onde falta medição. */
export function trechos(valores: readonly (number | null)[]): number[][] {
  const out: number[][] = [];
  let atual: number[] = [];
  valores.forEach((v, i) => {
    if (typeof v === 'number' && Number.isFinite(v)) atual.push(i);
    else if (atual.length) {
      out.push(atual);
      atual = [];
    }
  });
  if (atual.length) out.push(atual);
  return out;
}

/** Topo da escala: o maior valor com 10% de folga, nunca abaixo do piso. `null` sem nenhum valor. */
export function topoDaEscala(linhas: readonly Linha[], piso: number): number | null {
  let max = -Infinity;
  for (const l of linhas) for (const v of l.valores) if (typeof v === 'number' && Number.isFinite(v) && v > max) max = v;
  if (max === -Infinity) return null;
  return Math.max(piso, max * 1.1);
}

export function Grafico(props: {
  instantes: readonly number[];
  iAtual: number;
  linhas: readonly Linha[];
  referencias?: readonly Referencia[];
  piso: number;
  rotulo: string;
  valor: (i: number) => string | null;
  resumo: React.ReactNode;
  /** Linha cujo ponto acompanha a leitura (a principal). */
  principal?: number;
}) {
  const { instantes, linhas, piso } = props;
  const topo = useMemo(() => topoDaEscala(linhas, piso), [linhas, piso]);
  const partes = useMemo(() => linhas.map((l) => trechos(l.valores)), [linhas]);
  if (topo === null) return null;
  const n = instantes.length;
  const A = GRAFICO_A;
  const FOLGA = 1.5;
  const y = (v: number) => A - FOLGA - (Math.max(0, v) / topo) * (A - 2 * FOLGA);
  const principal = linhas[props.principal ?? 0];

  return (
    <GraficoTempo
      instantes={instantes}
      iAtual={props.iAtual}
      rotulo={props.rotulo}
      valor={props.valor}
      resumo={props.resumo}
      desenho={({ w, sob }) => (
        <>
          {(props.referencias ?? [])
            .filter((r) => r.valor < topo)
            .map((r) => (
              <line
                key={r.valor}
                x1="0"
                x2={n * w}
                y1={y(r.valor)}
                y2={y(r.valor)}
                stroke={r.cor}
                strokeOpacity="0.55"
                strokeWidth="0.8"
                strokeDasharray="2 2"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          {sob !== null && (
            <line
              x1={(sob + 0.5) * w}
              y1="0"
              x2={(sob + 0.5) * w}
              y2={A}
              stroke="rgba(255,255,255,0.35)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {linhas.map((l, k) =>
            partes[k]!.map((t) =>
              t.length === 1 ? (
                <line
                  key={`${k}-${t[0]}`}
                  x1={(t[0]! + 0.1) * w}
                  x2={(t[0]! + 0.9) * w}
                  y1={y(l.valores[t[0]!]!)}
                  y2={y(l.valores[t[0]!]!)}
                  stroke={l.cor}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ) : (
                <polyline
                  key={`${k}-${t[0]}`}
                  points={t.map((i) => `${((i + 0.5) * w).toFixed(2)},${y(l.valores[i]!).toFixed(2)}`).join(' ')}
                  fill="none"
                  stroke={l.cor}
                  strokeWidth={l.tracejado ? '1.1' : '1.5'}
                  strokeDasharray={l.tracejado ? '3 2' : undefined}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ),
            ),
          )}
        </>
      )}
      sobre={({ destaque }) => {
        const v = destaque === null || !principal ? null : principal.valores[destaque];
        if (destaque === null || typeof v !== 'number' || !Number.isFinite(v)) return null;
        return (
          <span
            aria-hidden
            className="pointer-events-none absolute size-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-ink-900"
            style={{ left: `${centroPct(destaque, n)}%`, top: `${(y(v) / A) * 100}%`, background: principal!.cor }}
          />
        );
      }}
    />
  );
}
